import { html, LitElement, TemplateResult } from 'lit';
import { customElement, property, query, state } from 'lit/decorators.js';
import { styles } from '../view/styles';
import { WEB_COMPONENT_GENERIC_DASHBOARD_TOOLBAR } from '../../../webcomponents.constants';
import { ButtonColor, ButtonSize, ButtonType, FontSize } from '../../../ui/gzIconButton/types';
import { SelectOptionValues, ToolbarButtonsTooltips, ToolbarEvents } from '../types';
import { OverlayPositionX, SelectOption } from '../../../ui/gzSelect/types';
import { connect } from 'pwa-helpers';
import { RootState, store } from '../../../redux/store';
import {
  filtersPanelActiveStateSelector,
  fullScreenActiveStateSelector,
  liveViewActiveStateSelector,
  selectShowExportButtonSpinner,
  selectHasFiltersToggle,
  settingsPanelActiveStateSelector,
  toggleFiltersPanelActiveState,
  toggleFullscreenActiveState,
  toggleLiveViewActiveState,
  toggleSettingsPanelActiveState,
  selectHasWidgetsInErrorState,
} from '../../redux/dashboards.ui.slice';
import { selectDashboardsAppState } from '../../redux/dashboards.app.reducer';
import { translateFn } from '../../../utils/translator.helper';
import { setRefreshWidgets } from '../../redux/dashboards.widgets.slice';
import { GzTooltipWebComponent } from '../../../ui/gzTooltip';

/**
 * @summary Component that can be used for different actions regarding generic dashboard.
 * @tag gz-dashboard-actions-toolbar
 */
@customElement(WEB_COMPONENT_GENERIC_DASHBOARD_TOOLBAR)
export class GzDashboardActionsToolbarWebComponent extends connect(store)(LitElement) {
  /**
   * Configure the availability of Live View button.
   * By default, Live View button <strong>is not</strong> available. <br>
   * The setter takes one parameter type <code>boolean</code>
   */
  @property({
    type: Boolean,
  })
  set hasLiveViewButton(hasLiveViewButton: boolean) {
    this.adjustComponentsStateWhenLiveViewButtonIsToggled(hasLiveViewButton);
  }

  /**
   * Configure the availability of Export as Pdf option.
   * By default, Export as Pdf option <strong>is not</strong> available. <br>
   * The setter takes one parameter type <code>boolean</code>
   */
  @property({
    type: Boolean,
  })
  set hasExportAsPdfOption(hasExportAsPdfOption: boolean) {
    this._hasExportAsPdfOption = hasExportAsPdfOption;
  }

  /**
   * Configure the availability of Export as Csv option.
   * By default, Export as Csv option <strong>is not</strong> available. <br>
   * The setter takes one parameter type <code>boolean</code>
   */
  @property({
    type: Boolean,
  })
  set hasExportAsCsvOption(hasExportAsCsvOption: boolean) {
    this._hasExportAsCsvOption = hasExportAsCsvOption;
  }

  /**
   * Configure the availability of Create Report button.
   * By default, Create Report button <strong>is not</strong> available. <br>
   * The setter takes one parameter type <code>boolean</code>
   */
  @property({
    type: Boolean,
  })
  set hasCreateReportButton(hasCreateReportButton: boolean) {
    this._hasCreateReportButton = hasCreateReportButton;
  }

  /**
   * Configure the usability of Live View button.
   * By default, Live View button <strong>is</strong> usable. <br>
   * The setter takes one parameter type <code>boolean</code>
   */
  @property({
    type: Boolean,
  })
  set liveViewButtonUsable(liveViewButtonUsable: boolean) {
    this.adjustComponentsStateWhenLiveViewUsabilityIsToggled(liveViewButtonUsable);
  }

  /**
   * @type {boolean}
   * Deactivate Live View button.
   * Notice: Live View cannot be set on true from the exterior.
   * Default is <strong>false</strong>.
   */
  @property({
    type: Boolean,
  })
  set liveViewButtonActive(liveViewButtonActive: boolean) {
    // we don't want to allow activating Live Search from the exterior
    if (liveViewButtonActive) return;
    this.adjustComponentWhenLiveViewButtonIsDeactivatedFromExterior();
  }

  /**
   * Configure the availability of View Settings button.
   * By default, View Settings button <strong>is</strong> available. <br>
   * The setter takes one parameter type <code>boolean</code>
   */
  @property({
    type: Boolean,
  })
  set hasViewSettingsButton(hasViewSettingsButton: boolean) {
    this._hasViewSettingsButton = hasViewSettingsButton;
  }

  /**
   * @internal
   */
  @state()
  private _hasLiveViewButton = false;
  /**
   * @internal
   */
  @state()
  private _hasExportAsPdfOption = false;
  /**
   * @internal
   */
  @state()
  private _hasExportAsCsvOption = false;
  /**
   * @internal
   */
  @state()
  private _hasCreateReportButton = false;
  /**
   * @internal
   */
  @state()
  private _liveViewButtonUsable = false;
  /**
   * @internal
   */
  @state()
  private _exportButtonActive = false;
  /**
   * @internal
   */
  @state()
  private _refreshButtonDisabled = false;
  /**
   * @internal
   */
  @state()
  private _hasViewSettingsButton = false;
  /**
   * @internal
   */
  @state()
  private _hasFilterToggle = true;
  /**
   * @internal
   */
  @state()
  private _liveViewTooltipLabel = '';
  /**
   * @internal
   */
  @state()
  private _exportTooltipLabel = '';
  /**
   * @internal
   */
  @state()
  private _createReportTooltipLabel = '';
  /**
   * @internal
   */
  @state()
  private _refreshTooltipLabel = '';
  /**
   * @internal
   */
  @state()
  private _settingsTooltipLabel = '';

  /**
   * @internal
   */
  private tooltipLabels: ToolbarButtonsTooltips;

  private _rootState: RootState;

  @state()
  private _settingsPanelActive: boolean;
  @state()
  private _fullScreenActive: boolean;
  @state()
  private _filtersPanelActive: boolean;
  @state()
  private _liveViewActive: boolean;
  @state()
  private _showExportButtonSpinner = false;
  @state()
  private _hasWidgetsInErrorState = true;

  @query('#expandViewButtonTooltip')
  private expandViewButtonTooltip: GzTooltipWebComponent;

  constructor() {
    super();
    this.setTooltipLabels();
    this._rootState = store.getState();
  }

  static get styles() {
    return styles;
  }

  public stateChanged(rootState: RootState) {
    const dashboardAppState = selectDashboardsAppState(rootState);
    this._settingsPanelActive = settingsPanelActiveStateSelector(dashboardAppState);
    this._fullScreenActive = fullScreenActiveStateSelector(dashboardAppState);
    this._filtersPanelActive = filtersPanelActiveStateSelector(dashboardAppState);
    this._liveViewActive = liveViewActiveStateSelector(dashboardAppState);
    this._hasFilterToggle = selectHasFiltersToggle(dashboardAppState);
    this._showExportButtonSpinner = selectShowExportButtonSpinner(dashboardAppState);
    this._hasWidgetsInErrorState = selectHasWidgetsInErrorState(dashboardAppState);
  }

  render(): TemplateResult {
    return html` <div class="generic-dashboard-toolbar">${this.renderButtons()}</div> `;
  }

  private renderButtons(): TemplateResult {
    return this._fullScreenActive
      ? html`${this.renderRefreshButton()} ${this.renderExpandViewButton()} ${this.renderFilterBarToggleButton()}`
      : html`
          ${this.renderLiveViewButtonIfAvailable()} ${this.renderRefreshButton()}
          ${this.renderExportButtonIfAvailable()} ${this.renderReportButtonIfAvailable()}
          ${this.renderExpandViewButton()} ${this.renderFilterBarToggleButton()} ${this.renderSettingsButtonIfAvailable()}
        `;
  }

  private renderRefreshButton(): TemplateResult {
    return html`
      <gz-tooltip tooltipText="${this._refreshTooltipLabel}">
        <gz-icon-button
          icon="ds-icon-refresh"
          id="refreshDashboardButton"
          buttontype=${ButtonType.SQUARE}
          buttoncolor=${ButtonColor.SECONDARY}
          .disabled=${this._refreshButtonDisabled}
          size=${ButtonSize.SIZE_32}
          fontSize=${FontSize.ICON_SIZE_24}
          @click=${() => this.treatButtonsEvents(ToolbarEvents.REFRESH_DASHBOARD_BUTTON_CLICKED)}
        ></gz-icon-button>
      </gz-tooltip>
    `;
  }

  private renderExpandViewButton(): TemplateResult {
    return html`
      <gz-tooltip id="expandViewButtonTooltip" tooltipText="${this.getExpandViewTooltipLabel()}">
        <gz-icon-button
          icon="ds-icon-fullscreen"
          id="expandViewButton"
          buttontype=${ButtonType.SQUARE}
          buttoncolor=${this._fullScreenActive ? ButtonColor.PRIMARY_ACTIVE : ButtonColor.SECONDARY}
          size=${ButtonSize.SIZE_32}
          fontSize=${FontSize.ICON_SIZE_24}
          @click=${() => this.treatButtonsEvents(ToolbarEvents.EXPAND_VIEW_BUTTON_CLICKED)}
        ></gz-icon-button>
      </gz-tooltip>
    `;
  }

  private renderSettingsButtonIfAvailable(): TemplateResult | undefined {
    if (!this._hasViewSettingsButton) {
      return;
    }

    return html`
      <gz-tooltip tooltipText="${this._settingsTooltipLabel}">
        <gz-icon-button
          icon="ds-icon-settings"
          id="settingsPanelButton"
          buttontype=${ButtonType.SQUARE}
          buttoncolor=${this._settingsPanelActive ? ButtonColor.PRIMARY_ACTIVE : ButtonColor.SECONDARY}
          size=${ButtonSize.SIZE_32}
          fontSize=${FontSize.ICON_SIZE_24}
          @click=${() => this.treatButtonsEvents(ToolbarEvents.SETTINGS_PANEL_BUTTON_CLICKED)}
        ></gz-icon-button>
      </gz-tooltip>
    `;
  }

  renderFilterBarToggleButton(): TemplateResult | undefined {
    if (!this._hasFilterToggle) {
      return;
    }

    return html`
      <gz-tooltip tooltipText="${this.getFiltersTooltipLabel()}">
        <gz-icon-button
          icon="ds-icon-filter_applied"
          id="filtersButton"
          buttontype=${ButtonType.SQUARE}
          buttoncolor=${this._filtersPanelActive ? ButtonColor.PRIMARY_ACTIVE : ButtonColor.SECONDARY}
          size=${ButtonSize.SIZE_32}
          fontSize=${FontSize.ICON_SIZE_24}
          @click=${() => this.treatButtonsEvents(ToolbarEvents.FILTERS_BUTTON_CLICKED)}
        ></gz-icon-button>
      </gz-tooltip>
    `;
  }

  private renderLiveViewButtonIfAvailable(): TemplateResult | undefined {
    if (!this._hasLiveViewButton) {
      return;
    }

    this._liveViewTooltipLabel = !this._liveViewActive
      ? this.tooltipLabels.activateLiveView
      : this.tooltipLabels.disableLiveView;

    return html`
      <gz-tooltip tooltipText="${this._liveViewTooltipLabel}">
        <gz-icon-button
          icon="ds-icon-live-view-button"
          id="liveViewButton"
          buttontype=${ButtonType.SQUARE}
          buttoncolor=${this._liveViewActive ? ButtonColor.PRIMARY_ACTIVE : ButtonColor.SECONDARY}
          .disabled=${!this._liveViewButtonUsable}
          size=${ButtonSize.SIZE_32}
          fontSize=${FontSize.ICON_SIZE_24}
          @click=${() => this.treatButtonsEvents(ToolbarEvents.LIVE_VIEW_BUTTON_CLICKED)}
        ></gz-icon-button>
      </gz-tooltip>
    `;
  }

  private renderReportButtonIfAvailable(): TemplateResult | undefined {
    if (!this._hasCreateReportButton) {
      return;
    }
    return html`
      <gz-tooltip tooltipText="${this._createReportTooltipLabel}">
        <gz-icon-button
          icon="ds-icon-reports"
          id="createReportButton"
          buttontype=${ButtonType.SQUARE}
          buttoncolor=${ButtonColor.SECONDARY}
          size=${ButtonSize.SIZE_32}
          fontSize=${FontSize.ICON_SIZE_24}
          @click=${() => this.treatButtonsEvents(ToolbarEvents.CREATE_REPORT_BUTTON_CLICKED)}
        ></gz-icon-button>
      </gz-tooltip>
    `;
  }

  private renderExportButtonIfAvailable(): TemplateResult | undefined {
    if (!this._hasExportAsCsvOption && !this._hasExportAsPdfOption) {
      return;
    }

    if (!this._hasExportAsPdfOption || !this._hasExportAsCsvOption) {
      return this.renderSingleOptionExportButton();
    }

    return html`
      <gz-select
        id="export-options-select"
        .options="${this.getExportOptionsConfig()}"
        @overlayClosed="${this.exportOptionsClosedListener}"
        @selectionChanged="${this.exportSelectionChangedListener}"
        overlayPositionX="${OverlayPositionX.LEFT}"
      >
        <gz-tooltip .tooltipText="${this._exportTooltipLabel}">
          <gz-icon-button
            icon="ds-icon-export"
            id="exportButton"
            buttontype=${ButtonType.SQUARE}
            buttoncolor=${this._exportButtonActive ? ButtonColor.PRIMARY_ACTIVE : ButtonColor.SECONDARY}
            size=${ButtonSize.SIZE_32}
            fontSize=${FontSize.ICON_SIZE_24}
            @click=${() => this.treatButtonsEvents(ToolbarEvents.EXPORT_BUTTON_CLICKED)}
          ></gz-icon-button>
        </gz-tooltip>
      </gz-select>
    `;
  }

  private setTooltipLabels() {
    this.tooltipLabels = {
      activateLiveView: translateFn('Live view'),
      disableLiveView: translateFn('Exit live view'),
      unavailableLiveView: translateFn('Live view is unavailable'),
      export: translateFn('Export'),
      createReport: translateFn('Create report'),
      refresh: translateFn('Refresh'),
      unavailableRefresh: translateFn('Refresh not available while in Live view mode'),
      openSettings: translateFn('Settings'),
      closeSettings: translateFn('Close settings'),
      activateExpandView: translateFn('Fullscreen'),
      disableExpandView: translateFn('Exit fullscreen'),
      showFilters: translateFn('Show filters'),
      hideFilters: translateFn('Hide filters'),
    };

    this._liveViewTooltipLabel = this.tooltipLabels.activateLiveView;
    this._exportTooltipLabel = this.tooltipLabels.export;
    this._createReportTooltipLabel = this.tooltipLabels.createReport;
    this._refreshTooltipLabel = this.tooltipLabels.refresh;
    this._settingsTooltipLabel = this.tooltipLabels.openSettings;
  }

  private renderSingleOptionExportButton(): TemplateResult {
    return html`
      <gz-tooltip .tooltipText="${this._exportTooltipLabel}">
        <gz-icon-button
          icon="ds-icon-export"
          id="exportButton"
          buttontype=${ButtonType.SQUARE}
          buttoncolor=${this._exportButtonActive ? ButtonColor.PRIMARY_ACTIVE : ButtonColor.SECONDARY}
          size=${ButtonSize.SIZE_32}
          fontSize=${FontSize.ICON_SIZE_24}
          .showSpinner="${this._showExportButtonSpinner}"
          .disabled="${this._hasWidgetsInErrorState}"
          @click=${this.exportSingleOptionListener}
        ></gz-icon-button>
      </gz-tooltip>
    `;
  }

  /**
   * @internal
   */
  private getExportOptionsConfig = (): Array<SelectOption> => {
    const options: Array<SelectOption> = [];

    if (this._hasExportAsPdfOption) {
      options.push({
        title: translateFn('Export PDF'),
        value: SelectOptionValues.EXPORT_PDF,
        customClass: 'info',
      });
    }

    if (this._hasExportAsCsvOption) {
      options.push({
        title: translateFn('Export CSV'),
        value: SelectOptionValues.EXPORT_CSV,
        customClass: 'info',
      });
    }

    return options;
  };

  private exportOptionsClosedListener() {
    this._exportButtonActive = false;
  }

  private exportSingleOptionListener() {
    let eventName: ToolbarEvents;

    if (this._hasExportAsPdfOption) {
      eventName = ToolbarEvents.EXPORT_PDF_OPTION_CLICKED;
    } else {
      eventName = ToolbarEvents.EXPORT_CSV_OPTION_CLICKED;
    }

    this.liveViewButtonActive = false;
    this._refreshButtonDisabled = false;
    store.dispatch(toggleLiveViewActiveState(false));

    this.dispatchToolbarEvent(eventName);
  }

  /**
   * This method handles the event emitted when an export selection is clicked
   * If live view mode is active, we should disable it and enable back refresh button
   * @param ev
   * @private
   */
  private exportSelectionChangedListener(ev) {
    const eventName =
      ev.detail.selected === SelectOptionValues.EXPORT_PDF
        ? ToolbarEvents.EXPORT_PDF_OPTION_CLICKED
        : ToolbarEvents.EXPORT_CSV_OPTION_CLICKED;

    this.liveViewButtonActive = false;
    this._refreshButtonDisabled = false;
    store.dispatch(toggleLiveViewActiveState(false));

    this.dispatchToolbarEvent(eventName);
  }

  private dispatchToolbarEvent(eventName: ToolbarEvents) {
    const newEvent = new CustomEvent(eventName, {
      bubbles: true,
      composed: true,
    });

    this.dispatchEvent(newEvent);
  }

  private treatButtonsEvents(eventName: ToolbarEvents): void {
    if (eventName === ToolbarEvents.EXPORT_BUTTON_CLICKED) {
      this._exportButtonActive = !this._exportButtonActive;
      return;
    }

    this.dispatchToolbarEvent(eventName);

    switch (eventName) {
      case ToolbarEvents.LIVE_VIEW_BUTTON_CLICKED:
        this.adjustComponentWhenLiveViewButtonIsClicked();
        break;
      case ToolbarEvents.EXPAND_VIEW_BUTTON_CLICKED:
        this.adjustComponentWhenExpandButtonIsClicked();
        break;
      case ToolbarEvents.SETTINGS_PANEL_BUTTON_CLICKED:
        this.adjustComponentWhenSettingsButtonIsClicked();
        break;
      case ToolbarEvents.FILTERS_BUTTON_CLICKED:
        this.adjustComponentWhenFiltersButtonIsClicked();
        break;
      case ToolbarEvents.REFRESH_DASHBOARD_BUTTON_CLICKED:
        store.dispatch(setRefreshWidgets(true));
        break;
      default:
        break;
    }
  }

  /**
   * This method handles the modifications that should occur when Settings button is clicked:
   * - toggle button's state and change its tooltip
   * - de-activate 'ExpandView' button (if active) and set tooltip accordingly
   * - de-activate 'LiveView' button (if active) and set tooltip accordingly
   * - activate 'Refresh' button if disabled by 'live view' mode
   * @private
   */
  private adjustComponentWhenSettingsButtonIsClicked() {
    this._settingsTooltipLabel = !this._settingsPanelActive
      ? this.tooltipLabels.openSettings
      : this.tooltipLabels.closeSettings;
    this._liveViewTooltipLabel = this.tooltipLabels.activateLiveView;
    this._refreshButtonDisabled = false;
    this._refreshTooltipLabel = this.tooltipLabels.refresh;

    // update redux store
    store.dispatch(toggleSettingsPanelActiveState(!this._settingsPanelActive));
  }

  /**
   * This method handles the modifications that should occur when ExpandView button is clicked:
   * - toggle button's state and change its tooltip
   * - de-activate 'Settings' button (if active) and set tooltip accordingly
   * @private
   */
  private adjustComponentWhenExpandButtonIsClicked() {
    // update local state
    this._settingsTooltipLabel = this.tooltipLabels.openSettings;

    // update redux store
    store.dispatch(toggleFullscreenActiveState(!this._fullScreenActive));

    if (this._fullScreenActive) {
      this.expandViewButtonTooltip.hideTooltip();
    }
  }

  /**
   * This method handles the modifications that should occur when LiveView button is clicked:
   * - toggle button's state and change its tooltip
   * - toggle 'Refresh' button's state and change its tooltip
   * - de-activate 'Settings' button (if active) and set tooltip accordingly
   * @private
   */
  private adjustComponentWhenLiveViewButtonIsClicked() {
    // update local state
    this._refreshButtonDisabled = !this._liveViewActive;
    this._refreshTooltipLabel = this._refreshButtonDisabled
      ? this.tooltipLabels.unavailableRefresh
      : this.tooltipLabels.refresh;
    this._settingsTooltipLabel = this.tooltipLabels.openSettings;

    // update redux store
    store.dispatch(toggleLiveViewActiveState(!this._liveViewActive));
  }

  private adjustComponentWhenLiveViewButtonIsDeactivatedFromExterior() {
    this._liveViewTooltipLabel = this.tooltipLabels.activateLiveView;
    this._refreshButtonDisabled = false;
    this._refreshTooltipLabel = this.tooltipLabels.refresh;

    store.dispatch(toggleLiveViewActiveState(false));
  }

  private adjustComponentWhenFiltersButtonIsClicked() {
    store.dispatch(toggleFiltersPanelActiveState(!this._filtersPanelActive));
  }

  private adjustComponentsStateWhenLiveViewButtonIsToggled(available: boolean) {
    const liveViewActiveWhenComputingState = liveViewActiveStateSelector(selectDashboardsAppState(this._rootState));

    this._hasLiveViewButton = available;
    this._liveViewTooltipLabel = this.tooltipLabels.activateLiveView;

    this._refreshButtonDisabled = false;
    this._refreshTooltipLabel = this.tooltipLabels.refresh;

    if (!available && liveViewActiveWhenComputingState) {
      store.dispatch(toggleLiveViewActiveState(available));
    }
  }

  private adjustComponentsStateWhenLiveViewUsabilityIsToggled(usable: boolean) {
    this._liveViewButtonUsable = usable;
    store.dispatch(toggleLiveViewActiveState(false));
    this._liveViewTooltipLabel = usable ? this.tooltipLabels.activateLiveView : this.tooltipLabels.unavailableLiveView;

    this._refreshButtonDisabled = false;
    this._refreshTooltipLabel = this.tooltipLabels.refresh;
  }

  private getExpandViewTooltipLabel(): string {
    // _fullScreenActive state can be modified not only by clicking on expand view button,
    // but also on ESC key press when in fullscreen mode
    return this._fullScreenActive ? this.tooltipLabels.disableExpandView : this.tooltipLabels.activateExpandView;
  }

  private getFiltersTooltipLabel(): string {
    return this._filtersPanelActive ? this.tooltipLabels.hideFilters : this.tooltipLabels.showFilters;
  }
}
